import type { WizardStatus } from '@risksmart-app/domain/src/types/consts/wizard-status';
import type { InsertWizardMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

type InsertWizardTRPCInput = {
  RiskId: string;
  AssessmentId?: string | null;
  ActivityId?: string | null;
  Status?: WizardStatus | null;
};

/**
 * Maps TRPC mutation response to match the GraphQL mutation structure
 */
const mapTrpcResponseToGraphQL = (trpcData: {
  RiskId: string;
}): InsertWizardMutation => {
  return {
    insertChildWizard: {
      __typename: 'InsertWizardOutput',
      RiskId: trpcData.RiskId,
    },
  };
};

export const useInsertWizardTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.wizard.insert.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.wizard.wizards.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.wizard.wizardById.queryKey(),
        });
      },
    }),
  });

  return {
    insertWizard: async (
      input: InsertWizardTRPCInput
    ): Promise<InsertWizardMutation> => {
      const result = await mutation.mutateAsync(input);

      return mapTrpcResponseToGraphQL(result);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
